<template>
  <div class="formulario-modelo">
    <div class="header-flex">
      <div>
        <h1>Resultados Consolidados</h1>
        <p v-if="nombreVersion" class="ctx-modelo">{{ nombreVersion }}</p>
        <p class="subtitulo">Versión #{{ idVersion }}</p>
      </div>
      <button class="btn salir" @click="$router.back()">Volver</button>
    </div>

    <div class="divider"></div>

    <div v-if="errorCarga" class="aviso aviso-error">{{ errorCarga }}</div>

    <div v-else-if="store.cargando" class="info">Cargando resultados…</div>

    <template v-else-if="store.resultados">
      <!-- Resumen global -->
      <div v-if="store.resultados.global" class="resumen-card">
        <h3>Resumen General</h3>
        <div class="resumen-grid">
          <div class="stat-card">
            <span class="stat-label">Calificación Global</span>
            <span class="stat-valor" :class="clasClase(store.resultados.global.clasificacion)">
              {{ store.resultados.global.valoracion != null ? store.resultados.global.valoracion.toFixed(2) : '—' }}
            </span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Porcentaje</span>
            <span class="stat-valor">{{ store.resultados.global.pct != null ? store.resultados.global.pct.toFixed(1) + '%' : '—' }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Grado</span>
            <span class="stat-valor">{{ store.resultados.global.grado ?? '—' }}</span>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs">
        <button class="tab" :class="{ activo: tab === 'indicadores' }" @click="tab = 'indicadores'">Por Aspecto</button>
        <button class="tab" :class="{ activo: tab === 'componentes' }" @click="tab = 'componentes'">Por Factor</button>
      </div>

      <!-- Vista por aspecto/indicador -->
      <div v-show="tab === 'indicadores'" class="contenedor-tabla">
        <table v-if="aspectos.length" class="lista-tabla">
          <thead>
            <tr>
              <th>Aspecto / Indicador</th>
              <th class="col-num">Valoración</th>
              <th>Grado</th>
              <th>Clasificación</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(a, i) in aspectos" :key="i">
              <td>{{ a.nombre }}</td>
              <td class="col-num">
                <span :class="clasClase(a.clasificacion)">
                  {{ a.valoracion != null ? a.valoracion.toFixed(2) : '—' }}
                </span>
              </td>
              <td>{{ a.grado ?? '—' }}</td>
              <td>
                <span class="badge-clas" :class="clasClase(a.clasificacion)">{{ clasLabel(a.clasificacion) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="mensaje-vacio">No hay resultados de aspectos.</p>
      </div>

      <!-- Vista por factor/componente -->
      <div v-show="tab === 'componentes'" class="contenedor-tabla">
        <table v-if="store.resultados.factores.length" class="lista-tabla">
          <thead>
            <tr>
              <th>Factor</th>
              <th class="col-num">Ponderación</th>
              <th class="col-num">Valoración</th>
              <th class="col-num">%</th>
              <th>Grado</th>
              <th>Clasificación</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in store.resultados.factores" :key="f.id_componente">
              <td>{{ f.nombre }}</td>
              <td class="col-num">{{ f.peso != null ? f.peso + '%' : '—' }}</td>
              <td class="col-num">
                <span :class="clasClase(f.clasificacion)">
                  {{ f.valoracion != null ? f.valoracion.toFixed(2) : '—' }}
                </span>
              </td>
              <td class="col-num">{{ f.pct != null ? f.pct.toFixed(1) + '%' : '—' }}</td>
              <td>{{ f.grado ?? '—' }}</td>
              <td>
                <span class="badge-clas" :class="clasClase(f.clasificacion)">{{ clasLabel(f.clasificacion) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="mensaje-vacio">No hay resultados de factores.</p>
      </div>
    </template>

    <div v-else-if="!store.cargando && !errorCarga" class="mensaje-vacio">
      No hay resultados disponibles para esta versión.
    </div>

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Resultados consolidados de una versión.
 *
 * ⚠️ Sin escopar por ejecución, los resultados agregan todos los programas y
 * periodos que hayan usado la versión.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/version/:id_version/resultados` (ResultadosConsolidados) — permiso `calificacion.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useGestionResultadosAutoevaluacion } from '@/store/GestionResultadosAutoevaluacion'
import { getProcesosService } from '@/services/ProcesoAutoevaluacionService'
import type { NodoResultado, Clasificacion } from '@/interfaces/ResultadosAutoevaluacion'
import MensajeFlotante from '@/components/MensajeFlotante.vue'

const route = useRoute()
const idVersion = Number(route.params.id_version)
const store = useGestionResultadosAutoevaluacion()
const nombreVersion = ref('')
const errorCarga = ref('')

const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('error')

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error' = 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

watch(() => store.error, (val) => { if (val) mostrarMensaje(val) })

const tab = ref<'indicadores' | 'componentes'>('indicadores')

interface AspectoFlat {
  nombre: string
  valoracion: number | null
  grado: string | null
  clasificacion: Clasificacion
}

function recolectarAspectos(nodo: NodoResultado): AspectoFlat[] {
  const list: AspectoFlat[] = []
  for (const a of nodo.aspectos) {
    list.push({ nombre: a.nombre, valoracion: a.valoracion, grado: a.grado, clasificacion: a.clasificacion })
  }
  for (const h of nodo.hijos) list.push(...recolectarAspectos(h))
  return list
}

const aspectos = computed<AspectoFlat[]>(() => {
  if (!store.resultados) return []
  const list: AspectoFlat[] = []
  for (const f of store.resultados.factores) list.push(...recolectarAspectos(f))
  return list
})

function clasClase(c: Clasificacion): string {
  return `clas-${c}`
}
function clasLabel(c: Clasificacion): string {
  return ({ fuerte: 'Fuerte', debil: 'Débil', muy_debil: 'Muy débil', no_aplica: 'No aplica' } as Record<string, string>)[c] ?? c
}

onMounted(async () => {
  if (!Number.isFinite(idVersion) || idVersion <= 0) {
    errorCarga.value = 'Versión no válida.'
    return
  }
  const procesos = await getProcesosService({ id_version: idVersion })
  if (!procesos.length) {
    errorCarga.value = 'No se encontró un proceso de autoevaluación para esta versión.'
    return
  }
  const proceso = procesos.find(p => p.estado !== 'finalizado') ?? procesos[procesos.length - 1]
  nombreVersion.value = (proceso as any)?.version?.nombre ?? ''
  if (!proceso.id) {
    errorCarga.value = 'El proceso no tiene un identificador válido.'
    return
  }
  await store.cargar(proceso.id)
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  width: 96%;
  max-width: 1100px;
  max-height: 100%;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px auto;
  font-family: var(--font-family);
}

.header-flex { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; }
h1 { font-size: 1.4rem; font-weight: 700; margin: 0; color: var(--text-primary); }
h3 { font-size: 1.1rem; margin: 0 0 0.75rem; }
.ctx-modelo { display: inline-block; margin: 6px 0 0; padding: 3px 10px; background: #e8f0f8; border-left: 3px solid #0e69a0; border-radius: 3px; font-size: 0.85rem; font-weight: 700; color: #111; }
.subtitulo { font-size: 0.85rem; color: #666; margin: 0.25rem 0 0; }
.divider { border: none; border-bottom: 1px solid #e0e0e0; margin: 12px 0 16px; }
.info { padding: 1rem 0; color: #555; }
.aviso { padding: 0.85rem 1rem; border-radius: var(--radius-md); font-size: 0.88rem; margin-bottom: 1rem; }
.aviso-error { background: #f8d7da; color: #721c24; }
.mensaje-vacio { color: #888; font-style: italic; padding: 1rem 0; }

/* Resumen */
.resumen-card { margin-bottom: 1.5rem; }
.resumen-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; }
.stat-card {
  border: 1px solid #e0e0e0; border-radius: 8px; padding: 1rem; text-align: center;
  display: flex; flex-direction: column; gap: 0.3rem;
}
.stat-label { font-size: 0.78rem; color: #666; text-transform: uppercase; letter-spacing: 0.03em; }
.stat-valor { font-size: 1.4rem; font-weight: 700; }

/* Tabs */
.tabs { display: flex; gap: 0; margin-bottom: 1rem; border-bottom: 2px solid #e0e0e0; }
.tab {
  padding: 0.55rem 1.1rem; font-weight: 600; border: none; background: transparent;
  cursor: pointer; font-family: var(--font-family); font-size: 0.88rem; color: #666;
  border-bottom: 2px solid transparent; margin-bottom: -2px; transition: all 0.15s ease;
}
.tab.activo { color: #0e69a0; border-bottom-color: #0e69a0; }
.tab:hover { color: #0e69a0; }

/* Table */
.contenedor-tabla { overflow-x: auto; }
.lista-tabla { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.lista-tabla th, .lista-tabla td {
  padding: 9px 12px; text-align: left; border-bottom: 1px solid #e0e0e0;
}
.lista-tabla thead { background-color: #0e69a0; color: white; text-transform: uppercase; font-size: 0.72rem; letter-spacing: 0.03em; }
.lista-tabla thead th { border-color: #0b587f; }
.col-num { text-align: right; white-space: nowrap; }

.badge-clas { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 0.72rem; font-weight: 600; }

/* Clasificación colores */
.clas-fuerte { color: #155724; }
.clas-debil { color: #856404; }
.clas-muy_debil { color: #721c24; }
.clas-no_aplica { color: #888; }

.badge-clas.clas-fuerte { background: #d4edda; }
.badge-clas.clas-debil { background: #fff3cd; }
.badge-clas.clas-muy_debil { background: #f8d7da; }
.badge-clas.clas-no_aplica { background: #f0f0f0; }

.btn {
  padding: 0.5rem 1rem; font-weight: 600; border: none; border-radius: 8px;
  cursor: pointer; transition: all 0.15s ease; font-family: var(--font-family); font-size: 0.85rem;
}
.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background-color: #d41a1a; color: white; }
</style>
